/**
 * A line over time (or over a video, for the retention curve): this period in the brand's line
 * colour, the previous period as a faint grey line behind it, a y axis from zero with round ticks,
 * hairline gridlines, a dot on the last point. When the last point is a day still going (today), the
 * line's last step is dashed, so a day that has barely begun does not read as a collapse. The pointer (or the arrow keys) moves a crosshair
 * that snaps to the nearest point; one readout lists every series there, value first.
 *
 * Accessible: the drawing is an image named by its title and a one-sentence description; the
 * keyboard readout is spoken; the numbers themselves are also in the card's table view (ReportCard).
 * Colours are tokens only (the brand's --accent-stroke for the line, neutrals for the rest).
 */

import { useId, useMemo, useRef, useState } from "react";
import { areaPath, labelIndexes, linear, linePath, maxOf, nearestIndex, positions, ticks } from "./scale";
import { useWidth } from "./use-width";

export interface LineSeries {
  id: string;
  /** The legend's and the readout's name ("This period", "Previous period"). */
  label: string;
  values: ReadonlyArray<number | null>;
  /** The current series is drawn in the brand colour; a comparison is a faint grey line behind it. */
  kind: "current" | "previous";
  /** The readout's heading for each point of this series, when it differs from the x label (the previous period's own days). */
  pointLabels?: readonly string[];
}

export interface LineChartProps {
  /** Names the drawing for assistive technology (the card shows its own heading). */
  title: string;
  /** One sentence saying what the chart shows (its trend, its peak). */
  description: string;
  /** One label per point along x. */
  labels: readonly string[];
  series: readonly LineSeries[];
  /** A value as the readout says it. */
  format: (value: number) => string;
  /** A tick on the y axis (compact). */
  tickFormat?: (value: number) => string;
  /** A fixed top for the y axis (1 for shares). */
  yMax?: number;
  /** Values are counts: ticks stay whole numbers. */
  integer?: boolean;
  /** A faint wash under the current line. */
  area?: boolean;
  height?: number;
  /** The current series' last point is a period still going (today so far): its last step is dashed. */
  partialLast?: boolean;
}

const PAD = { top: 12, right: 12, bottom: 28 };
const TICK_GAP = 8;

export function LineChart({ title, description, labels, series, format, tickFormat = format, yMax, integer = true, area, height = 240, partialLast }: LineChartProps) {
  const box = useRef<HTMLDivElement>(null);
  const width = useWidth(box);
  const ids = useId();
  const [index, setIndex] = useState<number | null>(null);
  const [byKeyboard, setByKeyboard] = useState(false);

  const yTicks = useMemo(() => ticks(yMax ?? maxOf(...series.map((s) => s.values)), 4, integer), [series, yMax, integer]);
  const top = yTicks[yTicks.length - 1]!;
  // The y labels' column is as wide as its longest tick (about 7px a character at caption size).
  const left = Math.max(...yTicks.map((t) => tickFormat(t).length)) * 7 + TICK_GAP + 4;
  const plotBottom = height - PAD.bottom;
  const xs = positions(labels.length, left, Math.max(left, width - PAD.right));
  const y = linear(0, top, plotBottom, PAD.top);
  const shown = labelIndexes(labels.length, Math.max(2, Math.floor((width - left) / 72)));
  const current = series.find((s) => s.kind === "current");
  const ordered = [...series].sort((a, b) => (a.kind === b.kind ? 0 : a.kind === "previous" ? -1 : 1));
  const lastIndex = current ? lastValueIndex(current.values) : -1;

  const pick = (i: number | null, keyboard: boolean) => { setIndex(i); setByKeyboard(keyboard); };
  const onPointer = (e: React.PointerEvent<SVGRectElement>) => {
    const rect = e.currentTarget.ownerSVGElement!.getBoundingClientRect();
    pick(nearestIndex(xs, e.clientX - rect.left), false);
  };
  const onKey = (e: React.KeyboardEvent) => {
    const n = labels.length;
    if (!n) return;
    const at = index ?? n - 1;
    const next = e.key === "ArrowRight" ? Math.min(n - 1, at + 1) : e.key === "ArrowLeft" ? Math.max(0, at - 1) : e.key === "Home" ? 0 : e.key === "End" ? n - 1 : null;
    if (e.key === "Escape") { pick(null, false); return; }
    if (next === null) return;
    e.preventDefault();
    pick(next, true);
  };

  const readout = index === null ? null : ordered.slice().reverse().map((s) => ({
    s, value: s.values[index], heading: s.pointLabels?.[index] ?? labels[index],
  }));
  const spoken = readout ? readout.map((r) => `${r.s.label}, ${r.heading}: ${typeof r.value === "number" ? format(r.value) : "no data"}`).join(". ") : "";
  const tipLeft = index === null ? 0 : xs[index]!;
  const flip = tipLeft > width * 0.6;

  return (
    <div className="flex flex-col gap-3">
      {series.length > 1 || partialLast ? (
        <ul className="flex flex-wrap gap-x-5 gap-y-1 ps-card-inset text-caption text-t-secondary" aria-label="Legend">
          {series.map((s) => (
            <li key={s.id} className="flex items-center gap-2">
              <svg width="16" height="4" aria-hidden="true"><line x1="1" y1="2" x2="15" y2="2" strokeWidth="2" strokeLinecap="round" className={s.kind === "current" ? "stroke-accent-stroke" : "stroke-s-highlight"} /></svg>
              {s.label}
            </li>
          ))}
          {partialLast ? (
            <li className="flex items-center gap-2">
              <svg width="16" height="4" aria-hidden="true"><line x1="1" y1="2" x2="15" y2="2" strokeWidth="2" strokeDasharray="4 4" className="stroke-accent-stroke" /></svg>
              Today so far
            </li>
          ) : null}
        </ul>
      ) : null}
      <div
        ref={box}
        className="relative rounded-2xl outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-s-focus"
        style={{ height }}
        tabIndex={0}
        aria-label={`${title}. Use the left and right arrow keys to read each point.`}
        onKeyDown={onKey}
        onFocus={() => { if (index === null && labels.length) pick(lastIndex >= 0 ? lastIndex : labels.length - 1, true); }}
        onBlur={() => pick(null, false)}
      >
        {width > 0 ? (
          <svg width={width} height={height} role="img" aria-labelledby={`${ids}-t ${ids}-d`} className="block overflow-visible">
            <title id={`${ids}-t`}>{title}</title>
            <desc id={`${ids}-d`}>{description}</desc>
            <g aria-hidden="true">
              {yTicks.map((t) => (
                <g key={t}>
                  <line x1={left} x2={width - PAD.right} y1={y(t)} y2={y(t)} strokeWidth="1" className="stroke-s-stroke2" shapeRendering="crispEdges" />
                  <text x={left - TICK_GAP} y={y(t)} dy="0.32em" textAnchor="end" className="fill-t-tertiary text-caption tabular-nums">{tickFormat(t)}</text>
                </g>
              ))}
              {shown.map((i) => (
                <text key={i} x={xs[i]} y={height - 8} textAnchor={shown.length > 1 && i === 0 ? "start" : i === labels.length - 1 && shown.length > 1 ? "end" : "middle"} className="fill-t-tertiary text-caption tabular-nums">{labels[i]}</text>
              ))}
              {area && current ? (
                <path d={areaPath(xs, current.values.map((v) => y(v ?? 0)), plotBottom)} className="fill-accent-stroke opacity-10" />
              ) : null}
              {ordered.map((s) => {
                const ys = s.values.map((v) => (typeof v === "number" ? y(v) : null));
                const dashLast = partialLast && s.kind === "current" && ys.length > 1;
                const cls = s.kind === "current" ? "stroke-accent-stroke" : "stroke-s-highlight";
                return (
                  <g key={s.id}>
                    <path d={linePath(xs, dashLast ? ys.map((v, i) => (i === ys.length - 1 ? null : v)) : ys)} fill="none" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" className={cls} />
                    {dashLast ? <path d={linePath(xs, ys.map((v, i) => (i >= ys.length - 2 ? v : null)))} fill="none" strokeWidth="2" strokeDasharray="4 4" strokeLinecap="round" className={cls} /> : null}
                  </g>
                );
              })}
              {current && lastIndex >= 0 && index === null ? (
                <circle cx={xs[lastIndex]} cy={y(current.values[lastIndex]!)} r="4" strokeWidth="2" className="fill-accent-stroke stroke-b-surface2" />
              ) : null}
              {index !== null ? (
                <>
                  <line x1={xs[index]} x2={xs[index]} y1={PAD.top} y2={plotBottom} strokeWidth="1" className="stroke-s-highlight" shapeRendering="crispEdges" />
                  {ordered.map((s) => typeof s.values[index] === "number" ? (
                    <circle key={s.id} cx={xs[index]} cy={y(s.values[index]!)} r="4" strokeWidth="2" className={`${s.kind === "current" ? "fill-accent-stroke" : "fill-t-tertiary"} stroke-b-surface2`} />
                  ) : null)}
                </>
              ) : null}
            </g>
            <rect x={left} y={0} width={Math.max(0, width - left - PAD.right)} height={height} fill="transparent" onPointerMove={onPointer} onPointerLeave={() => pick(null, false)} />
          </svg>
        ) : null}
        {readout ? (
          <div className="chart-tooltip pointer-events-none absolute top-0 z-10 flex min-w-36 flex-col gap-1 shadow-depth" aria-hidden="true"
            style={flip ? { insetInlineEnd: Math.max(0, width - tipLeft + 12) } : { insetInlineStart: tipLeft + 12 }}>
            {readout.map((r) => (
              <div key={r.s.id} className="flex items-center gap-2">
                <svg width="12" height="4" aria-hidden="true"><line x1="1" y1="2" x2="11" y2="2" strokeWidth="2" strokeLinecap="round" className={r.s.kind === "current" ? "stroke-accent-stroke" : "stroke-s-highlight"} /></svg>
                <span className="text-body-2 font-semibold tabular-nums">{typeof r.value === "number" ? format(r.value) : "—"}</span>
                <span className="text-caption opacity-80">{r.heading}</span>
              </div>
            ))}
          </div>
        ) : null}
        <div className="sr-only" aria-live="polite">{byKeyboard ? spoken : ""}</div>
      </div>
    </div>
  );
}

function lastValueIndex(values: ReadonlyArray<number | null>): number {
  for (let i = values.length - 1; i >= 0; i--) if (typeof values[i] === "number") return i;
  return -1;
}
